import { useEffect, useId, useMemo, useRef, useState, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { isIsoDate, weekdayName } from './format';

/*
 * Form and inline-edit primitives (spec 5.13): pickers not free text,
 * weekday beside dates, field-level messages under the field.
 */

const inputCls =
  'w-full rounded-md border border-line bg-white px-2.5 py-1.5 text-sm text-ink placeholder:text-ink-muted/70 focus:border-navy disabled:bg-band disabled:text-ink-muted';
const invalidCls = 'border-danger focus:border-danger';

export function Field({ label, hint, error, children, inline = false }: { label: string; hint?: string; error?: string | null; children: ReactNode; inline?: boolean }) {
  return (
    <label className={inline ? 'flex items-center gap-3 text-sm' : 'block text-sm'}>
      <span className={inline ? 'w-40 shrink-0 text-ink-muted' : 'mb-1 block text-xs font-semibold uppercase tracking-wide text-ink-muted'}>{label}</span>
      <span className="block min-w-0 flex-1">
        {children}
        {error ? <span className="mt-1 block text-xs text-danger">{error}</span> : hint ? <span className="mt-1 block text-xs text-ink-muted">{hint}</span> : null}
      </span>
    </label>
  );
}

export function TextInput({ invalid, className = '', ...rest }: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean; ref?: React.Ref<HTMLInputElement> }) {
  return <input {...rest} className={`${inputCls} ${invalid ? invalidCls : ''} ${className}`} />;
}

export function NumberInput({ invalid, className = '', ...rest }: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
  return <input type="number" inputMode="numeric" {...rest} className={`${inputCls} ${invalid ? invalidCls : ''} ${className}`} />;
}

/** Native date input; shows the weekday beside it once the date is valid. */
export function DateInput({
  value,
  onChange,
  invalid,
  showWeekday = true,
  className = '',
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  value: string | null;
  onChange(iso: string | null): void;
  invalid?: boolean;
  showWeekday?: boolean;
}) {
  return (
    <span className="flex items-center gap-2">
      <input
        type="date"
        {...rest}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className={`${inputCls} ${invalid ? invalidCls : ''} ${className}`}
      />
      {showWeekday && value && isIsoDate(value) && <span className="shrink-0 text-xs text-ink-muted">{weekdayName(value).slice(0, 3)}</span>}
    </span>
  );
}

export function Select({ invalid, className = '', children, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }) {
  return (
    <select {...rest} className={`${inputCls} ${invalid ? invalidCls : ''} ${className}`}>
      {children}
    </select>
  );
}

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange(v: boolean): void; label?: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${checked ? 'bg-navy' : 'bg-line'}`}
    >
      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  );
}

/**
 * A tick box with its words beside it, for a yes/no someone reads in a form.
 * (Switch keeps its label for screen readers only: it sits in table cells
 * under a column heading.)
 */
export function Checkbox({ checked, onChange, label, hint, disabled }: { checked: boolean; onChange(v: boolean): void; label: string; hint?: string; disabled?: boolean }) {
  return (
    <label className={`flex items-start gap-2 text-sm ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-navy" />
      <span>
        <span className="text-ink">{label}</span>
        {hint && <span className="block text-xs text-ink-muted">{hint}</span>}
      </span>
    </label>
  );
}

export interface ComboOption {
  value: string;
  label: string;
  /** Secondary text, e.g. the pilot code. */
  detail?: string;
  disabled?: boolean;
  disabledReason?: string;
}

/**
 * Searchable single-select (pilot by name with the code shown, tail, type).
 * Keyboard: type to filter, Up/Down to move, Enter to pick, Esc to close.
 */
export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Search',
  invalid,
  disabled,
  allowEmpty = false,
  autoFocus,
}: {
  options: ComboOption[];
  value: string | null;
  onChange(value: string | null): void;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
  allowEmpty?: boolean;
  autoFocus?: boolean;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? options.filter((o) => (o.label + ' ' + (o.detail ?? '')).toLowerCase().includes(q)) : options;
    return list.slice(0, 50);
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = (o: ComboOption) => {
    if (o.disabled) return;
    onChange(o.value);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={rootRef} className="relative">
      <div className={`flex items-center rounded-md border bg-white ${invalid ? 'border-danger' : 'border-line'} ${disabled ? 'bg-band' : ''}`}>
        <input
          ref={inputRef}
          role="combobox"
          aria-expanded={open}
          aria-controls={id + '-list'}
          aria-autocomplete="list"
          autoFocus={autoFocus}
          disabled={disabled}
          className="min-w-0 flex-1 bg-transparent px-2.5 py-1.5 text-sm outline-none placeholder:text-ink-muted/70"
          placeholder={selected ? '' : placeholder}
          value={open ? query : selected ? selected.label + (selected.detail ? ` (${selected.detail})` : '') : ''}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
              setActive((a) => Math.min(a + 1, filtered.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === 'Enter') {
              if (open && filtered[active]) {
                e.preventDefault();
                pick(filtered[active]);
              }
            } else if (e.key === 'Escape') {
              setOpen(false);
              setQuery('');
            } else if (e.key === 'Backspace' && !query && allowEmpty && value) {
              onChange(null);
            }
          }}
        />
        <ChevronDown size={14} className="mr-2 shrink-0 text-ink-muted" />
      </div>
      {open && (
        <ul id={id + '-list'} role="listbox" className="absolute z-30 mt-1 max-h-64 w-full min-w-[16rem] overflow-y-auto rounded-md border border-line bg-white py-1 text-sm shadow-lg">
          {allowEmpty && (
            <li role="option" aria-selected={value === null} className="cursor-pointer px-3 py-1.5 text-ink-muted hover:bg-band" onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange(null); setOpen(false); setQuery(''); }}>
              (none)
            </li>
          )}
          {filtered.length === 0 && <li className="px-3 py-1.5 text-ink-muted">No matches</li>}
          {filtered.map((o, i) => (
            <li
              key={o.value}
              role="option"
              aria-selected={o.value === value}
              aria-disabled={o.disabled}
              title={o.disabledReason}
              className={`flex cursor-pointer items-center justify-between gap-3 px-3 py-1.5 ${i === active ? 'bg-navy-soft' : 'hover:bg-band'} ${o.disabled ? 'cursor-not-allowed text-ink-muted' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(o)}
            >
              <span className="truncate">
                {o.label}
                {o.detail && <span className="ml-1 text-xs text-ink-muted">({o.detail})</span>}
                {o.disabled && o.disabledReason && <span className="ml-2 text-xs text-danger">{o.disabledReason}</span>}
              </span>
              {o.value === value && <Check size={14} className="shrink-0 text-navy" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/*
 * Inline table cells: show the value; click to edit; Enter or blur saves;
 * Esc cancels. Saving is the caller's job (autosave via a mutation).
 */

export function InlineText({
  value,
  onSave,
  placeholder = '',
  disabled,
  validate,
  mono = false,
  className = '',
}: {
  value: string;
  onSave(v: string): unknown;
  placeholder?: string;
  disabled?: boolean;
  validate?(v: string): string | null;
  mono?: boolean;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const commit = async () => {
    const err = validate ? validate(draft) : null;
    if (err) {
      setError(err);
      return;
    }
    setEditing(false);
    setError(null);
    if (draft !== value) await onSave(draft);
  };

  if (!editing) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setEditing(true)}
        className={`min-h-[1.75rem] w-full rounded px-1.5 py-0.5 text-left text-sm break-words [overflow-wrap:anywhere] hover:bg-band disabled:cursor-default disabled:hover:bg-transparent ${mono ? 'font-mono text-xs' : ''} ${className}`}
        title={disabled ? undefined : 'Click to edit'}
      >
        {value ? value : <span className="text-ink-muted/70">{placeholder || '—'}</span>}
      </button>
    );
  }
  return (
    <span className="block">
      <input
        autoFocus
        className={`${inputCls} py-0.5 ${error ? invalidCls : ''} ${mono ? 'font-mono text-xs' : ''}`}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          setError(null);
        }}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void commit();
          if (e.key === 'Escape') {
            setEditing(false);
            setDraft(value);
            setError(null);
          }
        }}
      />
      {error && <span className="block text-xs text-danger">{error}</span>}
    </span>
  );
}

export function InlineDate({
  value,
  onSave,
  disabled,
  allowEmpty = true,
  validate,
}: {
  value: string | null;
  onSave(v: string | null): unknown;
  disabled?: boolean;
  allowEmpty?: boolean;
  validate?(v: string | null): string | null;
}) {
  const [error, setError] = useState<string | null>(null);
  return (
    <span className="block">
      <span className="flex items-center gap-2">
        <input
          type="date"
          disabled={disabled}
          className={`${inputCls} py-0.5 ${error ? invalidCls : ''}`}
          value={value ?? ''}
          onChange={(e) => {
            const v = e.target.value || null;
            if (!v && !allowEmpty) {
              setError('Required');
              return;
            }
            const err = validate ? validate(v) : null;
            setError(err);
            if (!err) void onSave(v);
          }}
        />
        {value && isIsoDate(value) && <span className="shrink-0 text-xs text-ink-muted">{weekdayName(value).slice(0, 3)}</span>}
      </span>
      {error && <span className="block text-xs text-danger">{error}</span>}
    </span>
  );
}

export function InlineSelect<T extends string>({
  value,
  options,
  onSave,
  disabled,
  allowEmpty = false,
}: {
  value: T | null;
  options: { value: T; label: string }[];
  onSave(v: T | null): unknown;
  disabled?: boolean;
  allowEmpty?: boolean;
}) {
  return (
    <select
      disabled={disabled}
      className={`${inputCls} py-0.5`}
      value={value ?? ''}
      onChange={(e) => void onSave((e.target.value || null) as T | null)}
    >
      {allowEmpty && <option value="">—</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

export function InlineNumber({
  value,
  onSave,
  min,
  max,
  disabled,
  allowEmpty = false,
}: {
  value: number | null;
  onSave(v: number | null): unknown;
  min?: number;
  max?: number;
  disabled?: boolean;
  allowEmpty?: boolean;
}) {
  const [draft, setDraft] = useState(value === null ? '' : String(value));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setDraft(value === null ? '' : String(value)), [value]);
  const commit = () => {
    if (draft.trim() === '') {
      if (allowEmpty) {
        setError(null);
        if (value !== null) void onSave(null);
      } else setError('Required');
      return;
    }
    const n = Number(draft);
    if (!Number.isFinite(n) || !Number.isInteger(n)) return setError('Whole number');
    if (min !== undefined && n < min) return setError(`At least ${min}`);
    if (max !== undefined && n > max) return setError(`At most ${max}`);
    setError(null);
    if (n !== value) void onSave(n);
  };
  return (
    <span className="block">
      <input
        type="number"
        disabled={disabled}
        min={min}
        max={max}
        className={`${inputCls} w-24 py-0.5 ${error ? invalidCls : ''}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
        }}
      />
      {error && <span className="block text-xs text-danger">{error}</span>}
    </span>
  );
}

/** Weekday options for the fleet swap-day picker (Sunday = 0, matching the engine). */
export const WEEKDAY_OPTIONS = [0, 1, 2, 3, 4, 5, 6].map((d) => ({ value: String(d), label: weekdayName(d) }));
